/**
 * Application Constants
 * 
 * Centralized configuration values for validation rules, UI limits,
 * and business logic constraints. Keep all magic numbers here for
 * easy maintenance and consistent behavior across the app.
 */

/**
 * Validation rules for expense and recurring expense data
 */
export const VALIDATION_RULES = {
    /** Maximum allowed expense amount (prevents data entry errors) */
    MAX_AMOUNT: 1_000_000,

    /** Minimum allowed expense amount */
    MIN_AMOUNT: 0.01,

    /** Maximum length for description field */
    MAX_DESCRIPTION_LENGTH: 500,

    /** Maximum length for custom category names */
    MAX_CATEGORY_NAME_LENGTH: 50,
} as const;

/**
 * UI display limits and pagination settings
 */
export const UI_LIMITS = {
    /** Number of recent categories to show as quick chips */
    RECENT_CATEGORIES_COUNT: 4,

    /** Number of recent amounts to show as quick-tap buttons */
    RECENT_AMOUNTS_COUNT: 5,

    /** Number of recent expenses to show in "Quick Add Similar" panel */
    RECENT_EXPENSES_COUNT: 3,

    /** Number of days to look ahead for upcoming recurring expenses */
    UPCOMING_DAYS_WINDOW: 30,

    /** Time window (hours) for time-based category suggestions */
    TIME_SUGGESTION_WINDOW_HOURS: 1,

    /** Maximum number of years to show in year selector */
    MAX_YEAR_HISTORY: 10,
} as const;

/**
 * Date and time formats
 */
export const DATE_FORMATS = {
    /** ISO date format for database storage */
    ISO_DATE: 'YYYY-MM-DD',

    /** Display format for expense dates */
    DISPLAY_DATE: 'MMM D, YYYY',
} as const;

/**
 * Category-related constants
 */
export const CATEGORY_CONFIG = {
    /** Default theme for category icons/colors */
    DEFAULT_THEME: 'light' as const,

    /** Maximum number of custom categories per user */
    MAX_CUSTOM_CATEGORIES: 100,
} as const;

/**
 * Recurring expense frequencies
 * Used for validation and display
 */
export const RECURRING_FREQUENCIES = [
    'daily',
    'weekly',
    'monthly',
    'yearly',
] as const;

/**
 * Error messages for common validation failures
 * Keep user-facing messages consistent
 */
export const ERROR_MESSAGES = {
    AMOUNT_REQUIRED: 'Please enter a valid amount greater than 0',
    AMOUNT_TOO_LARGE: `Amount cannot exceed ${VALIDATION_RULES.MAX_AMOUNT.toLocaleString()}`,
    CATEGORY_REQUIRED: 'Please select a category',
    NOT_AUTHENTICATED: 'Not authenticated. Please sign in to continue.',
    GENERIC_ERROR: 'An unexpected error occurred. Please try again.',
    NETWORK_ERROR: 'Network error. Please check your connection.',
} as const;
